import { SortCategory, MultipleChoiceExamExaminerData } from '../../../../../../../store/workspace/types';
import { setExaminerReportData, setExaminerReportModal } from '../../../../../../../store/workspace/slice';
import { examStatusTitle } from '../../../../../../../utils/generalHelpers';
import { useDispatch } from '../../../../../../../store/store';
import CheckBox from '../../../../../../../components/CheckBox';
import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplicantSelection } from '../../../../hooks/useApplicantSelection';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import { useExaminerProfile } from '../../../../hooks/useExaminerProfile';
import styles from './style.module.scss';
import {
    DangerIcon,
    ProfileIcon,
    MessageIcon,
    DocumentIcon,
    ChevronUpIcon,
    ChevronDownIcon,
} from '../../../../../../../assets/icons';

type SortDirection = 'ascend' | 'descend';

interface SortState {
    category: SortCategory;
    direction: SortDirection;
};

/** Score columns, in table order, together with the total they are scored out of. */
const SCORE_COLUMNS: {
    title: string;
    group?: 'question-levels' | 'question-types';
    position?: 'start' | 'middle' | 'end';
    score: SortCategory;
    total: keyof MultipleChoiceExamExaminerData['scores'];
}[] = [
        { title: 'Grade', score: 'score_overall', total: 'max_score' },
        { title: 'Easy', group: 'question-levels', position: 'start', score: 'score_easy_level', total: 'n_questions_easy' },
        { title: 'Medium', group: 'question-levels', position: 'middle', score: 'score_medium_level', total: 'n_questions_medium' },
        { title: 'Hard', group: 'question-levels', position: 'end', score: 'score_hard_level', total: 'n_questions_hard' },
        { title: 'General', group: 'question-types', position: 'start', score: 'score_general', total: 'n_questions_general' },
        { title: 'Technical', group: 'question-types', position: 'middle', score: 'score_technical', total: 'n_questions_technical' },
        { title: 'Coding', group: 'question-types', position: 'end', score: 'score_coding', total: 'n_questions_coding' },
    ];

const LEVEL_COLUMNS = SCORE_COLUMNS.filter(column => column.group === 'question-levels');
const TYPE_COLUMNS = SCORE_COLUMNS.filter(column => column.group === 'question-types');

/** Turns `{ group: 'question-levels', position: 'start' }` into `data-group-start="question-levels"`. */
const groupAttribute = (column: typeof SCORE_COLUMNS[number]) =>
    column.group && column.position ? { [`data-group-${column.position}`]: column.group } : {};

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    examinersInfo: MultipleChoiceExamExaminerData[];
};

const MultipleChoiceExamTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { jobId, selectable, examinersInfo } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const openExaminerProfile = useExaminerProfile(jobId);
    const { isExpanded, toggleRow } = useExpandableRows<string>();
    // No sort until the user picks one: the API order is the default.
    const [sort, setSort] = useState<SortState | null>(null);

    const tableInfo = useMemo(() => {
        if (!sort) return examinersInfo;

        return [...examinersInfo].sort((a, b) => sort.direction === 'ascend'
            ? a.scores[sort.category] - b.scores[sort.category]
            : b.scores[sort.category] - a.scores[sort.category]);
    }, [examinersInfo, sort]);

    const { isSelected, toggleApplicant, toggleAll, allSelected } = useApplicantSelection(tableInfo);

    const openReportModal = (detail: MultipleChoiceExamExaminerData) => {
        dispatch(setExaminerReportData({
            status: detail.status,
            report: detail.feedback_report,
            fullName: `${detail.first_name} ${detail.family_name}`,
        }));

        dispatch(setExaminerReportModal(true));
    };

    const renderSortableHead = (column: typeof SCORE_COLUMNS[number]) => (
        <div className={styles.head_group}>
            <div className={styles.head_group_sort}>
                <ChevronUpIcon onClick={() => setSort({ category: column.score, direction: 'descend' })} />
                <ChevronDownIcon onClick={() => setSort({ category: column.score, direction: 'ascend' })} />
            </div>
            <span>{column.title}</span>
        </div>
    );

    const renderMobileScores = (
        info: MultipleChoiceExamExaminerData,
        title: string,
        columns: typeof SCORE_COLUMNS,
    ) => (
        <div className={styles.card_body_section}>
            <div className={styles.card_body_section_title}>
                {title}
            </div>
            {columns.map(column => <div key={column.title} className={styles.card_body_section_content}>
                <div className={styles.card_body_section_content_item}>
                    <span>{column.title}</span>
                    <span>{info.scores[column.score]}/{info.scores[column.total]}</span>
                </div>
            </div>)}
        </div>
    );

    return (
        <div className={styles.table} ref={ref}>
            <table>
                <thead>
                    <tr>
                        <th colSpan={3}>
                            {selectable && <div className={styles.select_all}>
                                <CheckBox onClick={toggleAll} checked={allSelected} />
                                <span>Select All ({tableInfo.length})</span>
                            </div>}
                        </th>
                        <th colSpan={3}>
                            <span className={styles.head_section}>Question levels</span>
                        </th>
                        <th colSpan={3}>
                            <span className={styles.head_section}>Question types</span>
                        </th>
                        <th colSpan={3}></th>
                    </tr>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Status</th>
                        {SCORE_COLUMNS.map(column => <th key={column.title} {...groupAttribute(column)}>
                            {renderSortableHead(column)}
                        </th>)}
                        <th data-noprint='true'>Report</th>
                        <th data-noprint='true'>Sheet &<br />feedback</th>
                        <th data-noprint='true'>Info</th>
                    </tr>
                </thead>
                <tbody>
                    {tableInfo.length === 0
                        ? <tr><td colSpan={10}>No data.</td></tr>
                        : tableInfo.map((info, index) => <tr key={info.userId}>
                            <td>{index + 1}</td>
                            <td>
                                <div className={styles.examinerName}>
                                    {selectable && <CheckBox
                                        onClick={() => toggleApplicant(info.userId)}
                                        checked={isSelected(info.userId)}
                                    />}
                                    <span>{info.first_name} {info.family_name}</span>
                                    {info.proctoring && <div className={styles.proctoring}>
                                        <DangerIcon />
                                        <span className={styles.proctoring_tooltip}>
                                            {info.first_name} {info.family_name}&nbsp;
                                            was out of the exam for&nbsp;
                                            {info.userInactiveTime ? parseFloat(info.userInactiveTime).toFixed(1) : 0} seconds.
                                        </span>
                                    </div>}
                                </div>
                            </td>
                            <td>
                                <span className={styles.status} data-status={info.status}>
                                    {examStatusTitle(info.status)}
                                </span>
                            </td>
                            {SCORE_COLUMNS.map(column => <td key={column.title} {...groupAttribute(column)}>
                                {info.scores[column.score]}/{info.scores[column.total]}
                            </td>)}
                            <td data-noprint='true'><MessageIcon onClick={() => openReportModal(info)} /></td>
                            <td data-noprint='true'><DocumentIcon onClick={() => navigate(`${info.userId}`)} /></td>
                            <td data-noprint='true'><ProfileIcon onClick={() => openExaminerProfile(info.userId)} /></td>
                        </tr>)}
                </tbody>
            </table>

            <div className={styles.mobile_list}>
                {tableInfo.map(info => <div className={styles.card} key={info.userId}>
                    <div className={styles.card_header} onClick={() => toggleRow(info.userId)}>
                        <span>{info.first_name} {info.family_name}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(info.userId) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(info.userId) && <div className={styles.card_body}>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Status</span>
                                <span className={styles.status} data-status={info.status}>
                                    {examStatusTitle(info.status)}
                                </span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Grade</span>
                                <span>{info.scores.score_overall}/{info.scores.max_score}</span>
                            </div>
                        </div>
                        {renderMobileScores(info, 'Question levels', LEVEL_COLUMNS)}
                        {renderMobileScores(info, 'Question types', TYPE_COLUMNS)}
                    </div>}
                </div>)}
            </div>
        </div>
    );
});

export default MultipleChoiceExamTable;
